import { useEffect, useMemo, useState } from 'react'

import { bindActionCreators } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { setActiveLanguage, addTranslationForLanguage } from 'react-localize-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { Alert } from 'antd'

import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { Routes } from '../../routes'
import AppLoading from '../../Components/shared/AppLoading'
import { restoreUserWithToken, RestoreUserWithTokenErrors } from '../../utils/api/user'
import { setLanguage } from '../../utils/locale'
import { LanguageLocales } from '../../utils/constants'
import { getErrorBody } from '../../utils/apiHelper'
import DefaultContainer from '../../HOC/DefaultContainer'

const Statuses = Object.freeze({
  PENDING: 'pending',
  SUCCESS: 'success',
  ERROR: 'error'
})

const StatusMessages = Object.freeze({
  [Statuses.SUCCESS]: 'redirect_account.messages.success',
  [Statuses.ERROR]: 'redirect_account.errors.'
})

const DEFAULT_DATA = { user: null, status: Statuses.PENDING, code: null }

const RestoreAccount = () => {
  const location = useLocation()
  const history = useHistory()
  const t = useSelector(selectTranslate)
  const dispatch = useDispatch()

  const dispatchSetActiveLanguage = useMemo(() => bindActionCreators(setActiveLanguage, dispatch), [dispatch])
  const dispatchAddTranslationForLanguage = useMemo(() => bindActionCreators(addTranslationForLanguage, dispatch), [dispatch])

  const [loadedLanguageTag, setLoadedLanguageTag] = useState('')
  const [data, setData] = useState(DEFAULT_DATA)

  const [, tokenId] = location.pathname.split(Routes.RESTORE_ACCOUNT + '/')

  const safeTranslate = (translateKey) => (loadedLanguageTag.length > 0 ? t(translateKey) : () => null)

  const setupLanguage = (language = LanguageLocales.FR_BE) => {
    setLanguage({
      language,
      addTranslationForLanguage: dispatchAddTranslationForLanguage,
      setActiveLanguage: dispatchSetActiveLanguage
    }).then(() => {
      setLoadedLanguageTag(language)
    })
  }

  useEffect(() => {
    setupLanguage(data.user?.language)
  }, [data.user?.language])

  useEffect(() => {
    if (data.status !== Statuses.PENDING) {
      setTimeout(() => history.push(Routes.LOGIN), 7000)
    }
  }, [data.status])

  useEffect(() => {
    if (tokenId) {
      restoreUserWithToken(tokenId).then(({ data }) => {
        setData({ user: data, status: Statuses.SUCCESS })
      }).catch((error) => getErrorBody(error).then(({ message }) => {
        const code = RestoreUserWithTokenErrors?.[message] ?? RestoreUserWithTokenErrors.OTHER

        setData({ status: Statuses.ERROR, code })
      }))
    } else {
      setData({ status: Statuses.ERROR, code: RestoreUserWithTokenErrors.NO_TOKEN_PROVIDED })
    }
  }, [tokenId])

  if (!loadedLanguageTag || data.status === Statuses.PENDING) {
    return <AppLoading />
  }

  return (
    <DefaultContainer>
      <div className='flex-fill' />

      <div className='flex-col jc-ctr ai-ctr'>
        <h3 className='mt-12 mb-12'>{safeTranslate('redirect.please_wait')}</h3>

        <div className='mt-12' />

        {data.status === Statuses.SUCCESS && (
          <div>
            <Alert
              description={safeTranslate('redirect_account.redirect_message')}
              message={safeTranslate(`${StatusMessages[data.status]}`)}
              type='info'
              showIcon
            />
          </div>
        )}
        {data.status === Statuses.ERROR && (
          <div>
            <Alert
              description={(
                <div className='mt-4 mb-4'>
                  <p>{safeTranslate('redirect_account.redirect_message')}</p>
                </div>
              )}
              message={safeTranslate(StatusMessages[data.status] + data.code)}
              type='warning'
              showIcon
            />
          </div>
        )}

        <div className='mt-16'>
          <span>{safeTranslate('redirect.question')}</span>
          <h3>{safeTranslate('redirect.link.login')}</h3>
        </div>
      </div>

      <div className='flex-fill' />

    </DefaultContainer>
  )
}

export default RestoreAccount
